/**
 * Âge d'une trame du backend au moment où le navigateur la traite, mesuré sans
 * faire confiance à l'horloge du poste.
 *
 * WHY (#44, 2026-10-20) : un onglet gelé (veille, économiseur de mémoire) reçoit
 * en rafale, des minutes plus tard, les trames que le serveur lui a écrites.
 * Une commande canvas ainsi livrée avait déjà été abandonnée par le serveur, et
 * le navigateur l'exécutait quand même. Chaque trame porte l'heure serveur de son
 * émission (`timestamp`) ; l'écart `heure locale − heure serveur` vaut décalage
 * d'horloge + latence. Le plus petit écart observé sur la socket approche le
 * décalage seul, et l'âge d'une trame est son écart moins ce plancher. Une rafale
 * livrée après un gel ne l'abaisse jamais : ses écarts sont tous grands.
 *
 * Deux corrections du plancher, toutes deux dans le sens qui laisse passer :
 * - il remonte de `FLOOR_DRIFT_PER_MS` par milliseconde, pour absorber une lente
 *   dérive de l'horloge. Pendant un gel de F ms, l'âge estimé vaut donc ~0,9 F ;
 * - un saut de l'horloge murale VERS L'AVANT (correction NTP en pas, heure changée
 *   à la main) se lit comme un écart entre l'horloge murale et l'horloge monotone
 *   (`performance.now()`) ; il est reporté sur le plancher. Sans cela, toutes les
 *   trames suivantes paraîtraient vieilles de la valeur du saut, et chaque écriture
 *   serait refusée pendant 10 × le saut. Un saut vers l'arrière n'a pas besoin de
 *   correction : il fait baisser les écarts, et la trame suivante recale le plancher.
 *
 * Limite assumée : pendant une VEILLE du système, l'horloge monotone s'arrête sur
 * certains OS alors que l'horloge murale continue. La veille se lit alors comme un
 * saut, est reportée, et une trame livrée au réveil n'est pas refusée — le
 * comportement d'avant ce garde. Le gel d'un onglet, cas de l'incident, laisse les
 * deux horloges avancer ensemble et reste détecté.
 */
const FLOOR_DRIFT_PER_MS = 0.1;
const WALL_CLOCK_JUMP_MS = 2_000;

/** Les deux horloges du poste au même instant : murale (`Date.now()`), monotone (`performance.now()`). */
export interface LocalClocks {
  wallMs: number;
  monoMs: number;
}

function parseServerTime(timestamp: string | undefined): number | null {
  if (typeof timestamp !== 'string') return null;
  const ms = Date.parse(timestamp);
  return Number.isFinite(ms) ? ms : null;
}

export class ServerFrameAgeEstimator {
  private floor: { deltaMs: number; at: LocalClocks } | null = null;

  observe(timestamp: string | undefined, now: LocalClocks): void {
    const serverMs = parseServerTime(timestamp);
    if (serverMs === null) return;
    const deltaMs = now.wallMs - serverMs;
    const current = this.floorAt(now);
    if (current === null || deltaMs <= current) this.floor = { deltaMs, at: now };
  }

  /** `null` quand rien ne permet de dater : l'appelant ne doit alors rien refuser. */
  ageMs(timestamp: string | undefined, now: LocalClocks): number | null {
    const serverMs = parseServerTime(timestamp);
    const floor = this.floorAt(now);
    if (serverMs === null || floor === null) return null;
    return Math.max(0, Math.round(now.wallMs - serverMs - floor));
  }

  private floorAt(now: LocalClocks): number | null {
    if (this.floor === null) return null;
    const monoElapsedMs = Math.max(0, now.monoMs - this.floor.at.monoMs);
    const wallJumpMs = (now.wallMs - this.floor.at.wallMs) - monoElapsedMs;
    return this.floor.deltaMs
      + (wallJumpMs > WALL_CLOCK_JUMP_MS ? wallJumpMs : 0)
      + FLOOR_DRIFT_PER_MS * monoElapsedMs;
  }
}
